import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Copy, Check, Download } from "lucide-react";
import pixService from "../../services/pixService";

const PixQrCodeModal = ({ open, onOpenChange, pix }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const payload = pix?.payload || pix?.pixCopiaECola;
    if (payload) {
      pixService.copyToClipboard(payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    const qrCodeUrl = pix?.qrcode || pix?.imagemQrcode;
    if (!qrCodeUrl) return;

    // Create a temporary link element and trigger download
    const link = document.createElement("a");
    link.href = qrCodeUrl;
    link.download = `qrcode-pix-${pix?.txid || "download"}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!pix) return null;

  const qrCodeUrl = pix.qrcodeUrl || pix.qrcode || pix.imagemQrcode;
  const payload = pix.payload || pix.pixCopiaECola;
  const amount = pix.amount || pix.valor;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">QR Code PIX</DialogTitle>
          <DialogDescription>
            Escaneie o código QR ou copie o código PIX Copia e Cola
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Informações do PIX */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-gray-600 font-medium mb-1">
                  ID da Transação
                </p>
                <p className="text-sm font-mono text-gray-900 break-all">
                  {pix.txid}
                </p>
              </div>

              {pix.orderCode && (
                <div>
                  <p className="text-xs text-gray-600 font-medium mb-1">
                    Pedido
                  </p>
                  <p className="text-sm font-bold text-blue-600">
                    #{pix.orderCode}
                  </p>
                </div>
              )}

              {pix.name && (
                <div className="md:col-span-2">
                  <p className="text-xs text-gray-600 font-medium mb-1">
                    Cliente
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {pix.name}
                  </p>
                </div>
              )}

              {pix.contact && (
                <div>
                  <p className="text-xs text-gray-600 font-medium mb-1">
                    Contato
                  </p>
                  <p className="text-sm font-mono text-gray-900">
                    {pixService.formatPhone(pix.contact)}
                  </p>
                </div>
              )}

              <div>
                <p className="text-xs text-gray-600 font-medium mb-1">Valor</p>
                <p className="text-lg font-bold text-[#c9332C]">
                  {amount ? pixService.formatCurrency(amount) : "-"}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-600 font-medium mb-1">Status</p>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    pix.status === "S" ||
                    pix.recebido ||
                    pix.status === "RECEIVED"
                      ? "bg-green-100 text-green-800"
                      : "bg-yellow-100 text-yellow-800"
                  }`}
                >
                  {pix.status === "S" ||
                  pix.recebido ||
                  pix.status === "RECEIVED"
                    ? "Pago"
                    : "Pendente"}
                </span>
              </div>

              {pix.createdAt && (
                <div>
                  <p className="text-xs text-gray-600 font-medium mb-1">
                    Data de Criação
                  </p>
                  <p className="text-sm text-gray-900">
                    {pixService.formatDate(pix.createdAt)}
                  </p>
                </div>
              )}

              {pix.confirmed_at && (pix.status === "S" || pix.recebido) && (
                <div>
                  <p className="text-xs text-gray-600 font-medium mb-1">
                    Data de Pagamento
                  </p>
                  <p className="text-sm text-green-700 font-semibold">
                    {pixService.formatDate(pix.confirmed_at)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* QR Code Image */}
          {qrCodeUrl && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">QR Code</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownload}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Baixar
                </Button>
              </div>
              <div className="flex justify-center bg-white p-6 rounded-lg border-2 border-gray-200 shadow-sm">
                <img
                  src={qrCodeUrl}
                  alt="QR Code PIX"
                  className="max-w-full h-auto"
                  style={{ maxHeight: "400px" }}
                  onError={(e) => {
                    e.target.src =
                      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect width='200' height='200' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='14' fill='%239ca3af'%3EImagem não disponível%3C/text%3E%3C/svg%3E";
                  }}
                />
              </div>
            </div>
          )}

          {/* PIX Copia e Cola */}
          {payload && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">
                PIX Copia e Cola
              </h3>
              <div className="relative">
                <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4 pr-16 max-h-40 overflow-y-auto">
                  <code className="text-xs break-all font-mono text-gray-700 leading-relaxed">
                    {payload}
                  </code>
                </div>
                <Button
                  variant={copied ? "default" : "outline"}
                  size="sm"
                  className={`absolute right-3 top-3 transition-all ${
                    copied
                      ? "bg-green-600 hover:bg-green-700 text-white"
                      : "text-gray-700 hover:text-gray-900 hover:bg-gray-100"
                  }`}
                  onClick={handleCopy}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Copiado!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-1" />
                      Copiar
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-600 italic">
                💡 Clique em "Copiar" e cole este código no app do seu banco
                para realizar o pagamento.
              </p>
            </div>
          )}

          {/* Alert quando não há QR code nem payload */}
          {!qrCodeUrl && !payload && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                ⚠️ Nenhum QR Code ou código PIX disponível para este registro.
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="min-w-[100px]"
          >
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PixQrCodeModal;
